import { FileDownload } from "@mui/icons-material";
import { Box, Button, Divider, Stack } from "@mui/material";
import { DataType, SortDirection } from "ka-table/enums";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import DropdownMenu from "../../../App/Components/DropdownMenu/DropdownMenu";
import { ExportAttachmentsModal } from "../../../App/Components/Export/ExportAttachmentsModal";
import Modal from "../../../App/Components/Modal/Modal";
import CrmTable from "../../../App/Components/Table/CrmTable";
import { exportCandidates, useExportCandidateAttachments } from "../../../App/Queries/CandidateQueries";
import { useSelectedClientCustomFields } from "../../../App/Queries/ClientQueries";
import { useAuthToken } from "../../../App/State/Hooks/authHooks";
import { useNotification } from "../../../App/State/Hooks/notificationHooks";
import { useAppSelector } from "../../../App/State/Hooks/reduxHooks";
import { useDebouncedHandler } from "../../../App/State/Hooks/useDebouncedHandler";
import { selectClientValue } from "../../../App/State/Slices/SelectedClientSlice";
import { IPaginationRequest } from "../../../App/Typings/Campaigns";
import { Field, ICandidate } from "../../../App/Typings/Candidate";
import { ICandidateProfile } from "../../../App/Typings/CandidateProfile";
import { IClientCustomField } from "../../../App/Typings/Client";
import { Rule } from "../../../App/Typings/DynamicGroupRules";
import { IPagination } from "../../../App/Typings/MessageText";
import { KaColumn } from "../../../App/Typings/TableColumn";
import CandidateTags from "../../../CandidateProfile/Components/CandidateAccordions/Subcomponents/CandidateTags";
import { useManageCandidateSelectionContext } from "../../State/ManageCandidateContext";
import BulkMessageModal from "../BulkMessageModal/BulkMessageModal";
import BulkTagModal from "../BulkTagModal/BulkTagModal";
import { CandidateActions } from "../CandidateActions/CandidateActions";

const DEFAULT_COLUMNS: KaColumn[] = [
  {
    title: "Date Added",
    key: "createdDate",
    dataType: DataType.Date,
    sortDirection: SortDirection.Descend,
    isSortable: true,
    visible: true,
    type: "default",
  },
  {
    title: "Name",
    key: "fullName",
    dataType: DataType.String,
    visible: true,
    type: "default",
  },
  {
    title: "Company",
    key: "company",
    dataType: DataType.String,
    visible: false,
    type: "default",
  },
  {
    title: "Title",
    key: "title",
    dataType: DataType.String,
    visible: false,
    type: "default",
  },
  {
    title: "Phone Numbers",
    key: "phoneNumbers",
    dataType: DataType.Object,
    visible: true,
    type: "default",
  },
  {
    title: "Email Addresses",
    key: "emailAddresses",
    dataType: DataType.Object,
    visible: true,
    type: "default",
  },
  {
    title: "Last Updated",
    key: "updatedDate",
    dataType: DataType.Date,
    visible: true,
    type: "default",
  },
  {
    title: "Latest Interacted",
    key: "lastInteractedWithDate",
    dataType: DataType.Date,
    visible: true,
    type: "default",
  },
  {
    title: "Tags",
    key: "tags",
    dataType: DataType.Object,
    visible: true,
    type: "default",
  },
  {
    title: "Locations",
    key: "locations",
    dataType: DataType.Object,
    visible: true,
    type: "default",
  },
  {
    title: "Notes",
    key: "notes",
    dataType: DataType.Object,
    visible: false,
    type: "default",
  },
  {
    title: "Attachments",
    key: "attachments",
    dataType: DataType.Object,
    visible: true,
    type: "default",
  },
];

const DEFAULT_ACTIONS_COLUMN: KaColumn = {
  title: "Actions",
  key: "actions",
  dataType: DataType.Object,
  isEditable: false,
  isSortable: false,
  isFilterable: false,
  width: 100,
  type: "action",
};

const DEFAULT_COLUMN_KEYS = new Set(DEFAULT_COLUMNS.map((col) => col.key));

const getColumnsWithCustomFields = (customColumns: IClientCustomField[]) => [
  ...DEFAULT_COLUMNS,
  ...customColumns.map((c) => {
    let key = c.key;
    if (DEFAULT_COLUMN_KEYS.has(c.key)) {
      key = `__${c.key}`;
    }
    return {
      key,
      dataType: DataType.String,
      title: c.label,
      visible: false,
      isSortable: false,
      type: "custom",
    } as KaColumn;
  }),
  DEFAULT_ACTIONS_COLUMN,
];

const getExportButtonLabel = ({
  selectAll,
  selectedCount,
  excludedCount,
  totalCount,
}: {
  selectAll: boolean;
  selectedCount: number;
  excludedCount: number;
  totalCount: number;
}): string => {
  if (selectAll) {
    if (excludedCount === 0) {
      return "Export All";
    }
    const exportCount = totalCount - excludedCount;
    return `Export All ${exportCount.toLocaleString()}`;
  }
  if (selectedCount > 0) {
    return `Export ${selectedCount.toLocaleString()} Candidate${selectedCount === 1 ? "" : "s"}`;
  }
  return "Export";
};

export function ManageCandidatesTable({
  candidates,
  candidatesLoading,
  pagination,
  handleChangePage,
  handleChangePageSize,
  handleRequestSort,
  search,
  rules,
}: {
  candidates: IPagination<ICandidate> | undefined;
  candidatesLoading: boolean;
  pagination: IPaginationRequest;
  handleChangePage: ((event: unknown, newPage: number) => void) | undefined;
  handleChangePageSize: (event: ChangeEvent<HTMLInputElement>) => void | undefined;
  handleRequestSort: (property: string) => void;
  search: string | undefined;
  rules: Rule[] | undefined;
}) {
  const token = useAuthToken();
  const navigate = useNavigate();
  const client = useAppSelector(selectClientValue);
  const { data: customFields = [] } = useSelectedClientCustomFields();
  const { displaySuccess, displayError } = useNotification();

  const {
    getters: {
      selectedCandidateIds,
      selectAll,
      excludedCandidateIds,
      isCandidateSelected,
      currentPageSelectedIds,
      selectedCount,
    },
    setters: { setActionCandidate, setTotalAvailableCount },
    handlers: { handleSelectRow, handleSelectAllClick },
  } = useManageCandidateSelectionContext();
  const [editTagsModalOpen, setEditTagsModalOpen] = useState<boolean>(false);
  const [tagEditCandidate, setTagEditCandidate] = useState<ICandidate>();
  const [exportAttachmentsModalOpen, setExportAttachmentsModalOpen] = useState<boolean>(false);

  // Update total available count when candidates data changes
  useEffect(() => {
    if (candidates?.totalItems !== undefined) {
      setTotalAvailableCount(candidates.totalItems);
    }
  }, [candidates?.totalItems, setTotalAvailableCount]);

  // Compute current page selected candidates based on selection state
  const currentPageSelectedCandidates = useMemo(() => {
    if (selectAll) {
      // When selectAll is true, all current page candidates are selected except excluded ones
      return (candidates?.items ?? []).filter((c) => !excludedCandidateIds.includes(c.id));
    }
    // Otherwise, only explicitly selected candidates from current page
    return (candidates?.items ?? []).filter((c) => currentPageSelectedIds.includes(c.id));
  }, [candidates?.items, selectAll, excludedCandidateIds, currentPageSelectedIds]);

  const openModal = (candidate: ICandidate) => {
    setTagEditCandidate(candidate);
    setEditTagsModalOpen(true);
  };

  const candidateData = useMemo(
    () =>
      (candidates?.items ?? []).map((candidate: ICandidate) => {
        var data = {
          ...candidate,
          actions: (
            <CandidateActions
              onEditTagsClick={openModal}
              candidate={candidate}
              setSelectedCandidate={setActionCandidate}
            />
          ),
        } as any;

        customFields.forEach((c) => {
          const accessKey = DEFAULT_COLUMN_KEYS.has(c.key) ? `__${c.key}` : c.key;
          data[accessKey] = printValue(data.fields.filter((f: Field) => f.key === c.key)[0]?.value);
        });

        return data;
      }),
    [candidates, customFields],
  );

  const allColumns: KaColumn[] = useMemo(() => {
    let cols = getColumnsWithCustomFields(customFields);

    cols.forEach((c) => {
      if (c.key === pagination.sortBy) {
        c.sortDirection = pagination.sortDirection === "desc" ? SortDirection.Descend : SortDirection.Ascend;
      } else {
        c.sortDirection = undefined;
      }
    });

    return cols;
  }, [client, pagination.sortBy, pagination.sortDirection, customFields]);

  const { mutateAsync: exportAttachments } = useExportCandidateAttachments();

  const handleExportAttachments = useDebouncedHandler(async () => {
    await exportAttachments(
      {
        rules: rules ? JSON.stringify(rules) : undefined,
        selectAll,
        candidateIds: selectedCandidateIds,
        excludedIds: excludedCandidateIds,
        search,
      },
      {
        onSuccess: (successResponse) => {
          if (successResponse.success) {
            displaySuccess("Exporting attachments, you will receive an email shortly.");
          }
          if (successResponse.message) {
            displayError(successResponse.message ?? "Error exporting attachments, please try again.");
          }
        },
        onError: () => {
          displayError("Error exporting attachments, please contact support.");
        },
      },
    );
  }, 300);

  return (
    <>
      <Stack
        sx={{ width: "100%" }}
        direction="row"
        justifyContent={"flex-end"}
        divider={<Divider orientation="vertical" flexItem variant="middle" />}
      >
        <BulkMessageModal
          selectedCandidates={currentPageSelectedCandidates}
          selectedCount={currentPageSelectedCandidates.length}
        />
        <BulkTagModal
          selectedCandidates={currentPageSelectedCandidates.map((c) => c.id)}
          selectedCount={currentPageSelectedCandidates.length}
        />
      </Stack>
      <CrmTable
        name="manage-candidates"
        loading={candidatesLoading}
        columns={allColumns}
        data={candidateData ?? []}
        onSort={handleRequestSort}
        pagination={{
          rowsPerPage: [10, 25, 50, 100],
          enabled: true,
          totalCount: candidates?.totalItems,
          currentPage: pagination.page,
          rows: pagination.pageSize,
          handleChangePage,
          handleChangeRowsPerPage: handleChangePageSize,
        }}
        order={pagination.sortDirection === "desc" ? "desc" : "asc"}
        orderBy={pagination.sortBy}
        onRowClick={(row) => navigate(`/candidates/${row.id}`)}
        rowTestId={(row) => `candidate-${row.id}`}
        rowSelection={{
          onRowSelect: (row: ICandidate, selected: boolean) => handleSelectRow(row, selected),
          isRowSelected: (row: ICandidate) => isCandidateSelected(row.id),
          onAllSelected: (selected: boolean) => {
            handleSelectAllClick(selected, candidates?.items);
          },
        }}
        ExportButton={(cols) => {
          const exportLabel = getExportButtonLabel({
            selectAll,
            selectedCount,
            excludedCount: excludedCandidateIds.length,
            totalCount: candidates?.totalItems ?? 0,
          });
          return (
            <DropdownMenu
              buttonLabel={exportLabel}
              tooltipText="Export candidate data or attachments"
              buttonIcon={<FileDownload color="primary" />}
              menuItems={[
                {
                  label: "Candidate Data",
                  title: "Export candidate data using your current filters",
                  onClick: async () =>
                    await exportCandidates(token, client.clientId, {
                      rules: rules ? JSON.stringify(rules) : undefined,
                      columns: (cols?.filter((c) => c.visible) ?? []).map((c) => c.key),
                      selectAll,
                      candidateIds: selectedCandidateIds,
                      excludedIds: excludedCandidateIds,
                      search,
                    }),
                },
                {
                  label: "Candidate Attachments",
                  title: "Export candidate attachments using your current filters",
                  onClick: () => setExportAttachmentsModalOpen(true),
                },
              ]}
            />
          );
        }}
      />
      <Modal
        content={
          <Box data-test="candidate-tags">
            <CandidateTags candidate={tagEditCandidate as ICandidateProfile} optimisticLoading={false} />
          </Box>
        }
        title="Edit Candidate Tags"
        dataTestId="candidate-tag-edit"
        footerContent={
          <Button color="info" variant="contained" onClick={() => setEditTagsModalOpen(false)}>
            Close
          </Button>
        }
        isOpen={editTagsModalOpen}
        handleCloseModal={() => setEditTagsModalOpen(false)}
        size="md"
      />
      <ExportAttachmentsModal
        isOpen={exportAttachmentsModalOpen}
        handleCloseModal={() => setExportAttachmentsModalOpen(false)}
        onExportAttachmentsClick={() => {
          handleExportAttachments();
          setExportAttachmentsModalOpen(false);
        }}
      />
    </>
  );
}

const printValue = (value: any) => {
  if (value) {
    return Array.isArray(value) ? value.join(", ") : value;
  }

  return "";
};
